import { getQwanta } from './getQwanta';
import { persistQwanta } from './persistQwanta';
import { newId } from './newId';

/**
 * Duplicates a qwanta by creating a copy with a new ID
 * 
 * @param {string|ObjectId} sourceId - The _id of the qwanta to duplicate
 * @param {Object} [overrideProps={}] - Optional properties to override in the duplicated qwanta
 * @param {boolean} [backup=false] - Whether to create a backup of the changes
 * @param {string} [title] - New title to use instead of the original
 * @param {string} [titleAppend] - String to append to the original title
 * @returns {Promise<{success: boolean, newId: string|null, error: Error|null}>} - Result object with new ID
 */

export async function dupQwanta(sourceId, ed, overrideProps = {}, backup = false, title, titleAppend) {
  try {
    // Step 1: Fetch the original qwanta
    const originalQwanta = await getQwanta(sourceId);
    
    if (!originalQwanta) {
      throw new Error(`No qwanta found with id: ${sourceId}`);
    }
    
    // Step 2: Generate a new ID
    const duplicateId = newId();
    
    // Step 3: Create a duplicate qwanta object without the original _id
    const { _id, createdAt, backup: originalBackup, ...qwantaProperties } = originalQwanta;
    
    // Step 4: Handle title modifications
    if (title) {
      // If title is provided, use it directly
      qwantaProperties.title = title;
    } else if (titleAppend && qwantaProperties.title) {
      // If titleAppend is provided, append it to the original title
      qwantaProperties.title = `${qwantaProperties.title} ${titleAppend}`;
    }
    
    // Step 5: Apply any override properties
    const duplicateProperties = {
      ...qwantaProperties,
      ...overrideProps,
      duplicatedFrom: sourceId, // Optional: keep track of the source
      duplicatedAt: new Date()  // Optional: record when it was duplicated
    };
    
    // Step 6: Save the duplicate qwanta with the new ID
    await persistQwanta(duplicateId, duplicateProperties, backup);
    
    return {
      success: true,
      newId: duplicateId,
      error: null
    };
  } catch (error) {
    console.error("Error duplicating qwanta:", error);
    return {
      success: false,
      newId: null,
      error
    };
  }
}